/**
 * Pure rules for Distillation Defender.
 *
 * VID strips only A1 violators. A raw "strip every generator" move is absent:
 * A2/A3/A4 generators remain part of the immune core while the player aims the
 * projector at A1 cells in the distillation band.
 *
 * SPDX-License-Identifier: LicenseRef-Proprietary
 * Copyright (c) 2026 Invariant Holdings LLC
 */

import levelsData from "../data/distillationDefenderLevels.json" with { type: "json" };

export type DefenderAxiom = "A1" | "A2" | "A3" | "A4";
export type DefenderGeneratorKind = "violator" | "immune";
export type DistillationDefenderStatus = "playing" | "cleared" | "escaped";

export interface DistillationStripZone {
  readonly start: number;
  readonly end: number;
}

export interface DefenderGeneratorSpec {
  readonly id: string;
  readonly label: string;
  readonly axiom: DefenderAxiom;
  readonly kind: DefenderGeneratorKind;
  readonly lane: number;
  readonly y: number;
  readonly speed: number;
}

export interface DefenderGenerator extends DefenderGeneratorSpec {
  readonly y: number;
}

export interface DistillationDefenderLevel {
  readonly id: string;
  readonly name: string;
  readonly brief: string;
  readonly lanes: readonly string[];
  readonly stripZone: DistillationStripZone;
  readonly generators: readonly DefenderGeneratorSpec[];
}

export interface DistillationDefenderState {
  readonly levelId: string;
  readonly levelName: string;
  readonly brief: string;
  readonly playerLane: number;
  readonly generators: readonly DefenderGenerator[];
  readonly strippedIds: readonly string[];
  readonly preservedIds: readonly string[];
  readonly escapedIds: readonly string[];
  readonly shots: number;
  readonly misses: number;
  readonly status: DistillationDefenderStatus;
  readonly lastEvent: string;
}

export type DistillationDefenderMove =
  | { readonly type: "move"; readonly lane: number }
  | { readonly type: "strip"; readonly targetId?: string };

interface DistillationDefenderLevelsData {
  readonly levels: readonly DistillationDefenderLevel[];
}

const EXIT_Y = 96;

export const DISTILLATION_DEFENDER_LEVELS = (
  levelsData as DistillationDefenderLevelsData
).levels;
export const DEFAULT_DISTILLATION_DEFENDER_LEVEL_ID =
  DISTILLATION_DEFENDER_LEVELS[0]?.id ?? "";

const LEVEL_BY_ID = new Map(
  DISTILLATION_DEFENDER_LEVELS.map((level) => [level.id, level]),
);

export function getDistillationDefenderLevel(
  levelId: string,
): DistillationDefenderLevel {
  const level = LEVEL_BY_ID.get(levelId);
  if (!level)
    throw new Error(`Unknown Distillation Defender level: ${levelId}`);
  return level;
}

export function createDistillationDefenderState(
  levelId = DEFAULT_DISTILLATION_DEFENDER_LEVEL_ID,
): DistillationDefenderState {
  const level = getDistillationDefenderLevel(levelId);
  return {
    levelId: level.id,
    levelName: level.name,
    brief: level.brief,
    playerLane: Math.floor(level.lanes.length / 2),
    generators: level.generators.map((generator) => ({ ...generator })),
    strippedIds: [],
    preservedIds: [],
    escapedIds: [],
    shots: 0,
    misses: 0,
    status: "playing",
    lastEvent:
      "Aim at A1 violators in the distillation band. A2/A3/A4 generators are preserved.",
  };
}

export function tickDistillationDefender(
  state: DistillationDefenderState,
  deltaMs: number,
): DistillationDefenderState {
  if (state.status !== "playing") return state;

  const dt = Math.max(0, Math.min(deltaMs, 80)) / 1000;
  const stripped = new Set(state.strippedIds);
  const preserved = new Set(state.preservedIds);
  const escaped = new Set(state.escapedIds);
  const nextGenerators = state.generators.map((generator) => {
    if (
      stripped.has(generator.id) ||
      preserved.has(generator.id) ||
      escaped.has(generator.id)
    ) {
      return generator;
    }
    return { ...generator, y: generator.y + generator.speed * dt };
  });

  let lastEvent = state.lastEvent;
  for (const generator of nextGenerators) {
    if (
      generator.y < EXIT_Y ||
      stripped.has(generator.id) ||
      preserved.has(generator.id) ||
      escaped.has(generator.id)
    ) {
      continue;
    }
    if (generator.kind === "immune") {
      preserved.add(generator.id);
      lastEvent = `${generator.label} stayed in the immune core.`;
    } else {
      escaped.add(generator.id);
      lastEvent = `${generator.label} exited before VID stripped the A1 violation.`;
    }
  }

  const next: DistillationDefenderState = {
    ...state,
    generators: nextGenerators,
    preservedIds: [...preserved],
    escapedIds: [...escaped],
    lastEvent,
  };

  if (next.escapedIds.length > 0) {
    return { ...next, status: "escaped" };
  }
  if (allViolatorsStripped(next)) {
    return {
      ...next,
      status: "cleared",
      lastEvent:
        "All A1 violators were stripped; the remaining generators form the immune core.",
    };
  }
  return next;
}

export function legalDistillationDefenderMoves(
  state: DistillationDefenderState,
): readonly DistillationDefenderMove[] {
  if (state.status !== "playing") return [];
  const level = getDistillationDefenderLevel(state.levelId);
  const laneMoves = level.lanes.map((_, lane) => ({
    type: "move" as const,
    lane,
  }));
  return [...laneMoves, ...legalDistillationShots(state)];
}

export function legalDistillationShots(
  state: DistillationDefenderState,
): readonly Extract<DistillationDefenderMove, { readonly type: "strip" }>[] {
  if (state.status !== "playing") return [];
  const level = getDistillationDefenderLevel(state.levelId);
  const inactive = inactiveGeneratorIds(state);
  const targets = state.generators
    .filter((generator) => !inactive.has(generator.id))
    .filter((generator) => generator.kind === "violator")
    .filter((generator) => generator.lane === state.playerLane)
    .filter(
      (generator) =>
        generator.y >= level.stripZone.start &&
        generator.y <= level.stripZone.end,
    )
    .sort((a, b) => b.y - a.y);
  if (targets.length === 0) return [];
  return [{ type: "strip", targetId: targets[0].id }];
}

export function stepDistillationDefender(
  state: DistillationDefenderState,
  move: DistillationDefenderMove,
): DistillationDefenderState {
  if (state.status !== "playing") return state;
  const level = getDistillationDefenderLevel(state.levelId);

  if (move.type === "move") {
    const lane = clamp(move.lane, 0, level.lanes.length - 1);
    return {
      ...state,
      playerLane: lane,
      lastEvent: `Projector aligned with ${level.lanes[lane]}.`,
    };
  }

  const legal = legalDistillationShots(state).find(
    (candidate) =>
      move.targetId === undefined || candidate.targetId === move.targetId,
  );
  if (!legal?.targetId) {
    return {
      ...state,
      shots: state.shots + 1,
      misses: state.misses + 1,
      lastEvent: "No A1 violator is constructable in the distillation band.",
    };
  }

  const target = state.generators.find(
    (generator) => generator.id === legal.targetId,
  );
  const strippedIds = [...state.strippedIds, legal.targetId];
  const next: DistillationDefenderState = {
    ...state,
    strippedIds,
    shots: state.shots + 1,
    lastEvent: `${target?.label ?? legal.targetId} stripped from the active generator set.`,
  };

  if (allViolatorsStripped(next)) {
    return {
      ...next,
      status: "cleared",
      lastEvent:
        "All A1 violators were stripped; the remaining generators form the immune core.",
    };
  }
  return next;
}

export function stripAllGeneratorsMoveGenerated(): boolean {
  return false;
}

export function activeDistillationGenerators(
  state: DistillationDefenderState,
): readonly DefenderGenerator[] {
  const inactive = inactiveGeneratorIds(state);
  return state.generators.filter((generator) => !inactive.has(generator.id));
}

export function remainingA1Violators(state: DistillationDefenderState): number {
  return activeDistillationGenerators(state).filter(
    (generator) => generator.kind === "violator",
  ).length;
}

function allViolatorsStripped(state: DistillationDefenderState): boolean {
  return remainingA1Violators(state) === 0;
}

function inactiveGeneratorIds(
  state: DistillationDefenderState,
): ReadonlySet<string> {
  return new Set([
    ...state.strippedIds,
    ...state.preservedIds,
    ...state.escapedIds,
  ]);
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}
